import { hashKey, type QueryKey } from '@tanstack/react-query';
import { omit } from 'lodash';
import type { BatchParams } from '@dealstack/frontend/app/services/dealstackAi/dealstackAi.types';
import { isRecord } from '../../dealstackAi.utils';

/** The deal header every call sends; it comes from the params, so the hooks work outside a deal page. */
export const dealHeaders = (dealId: string) => ({ 'deal-id': dealId });

/** A request to batch `batchId`. */
export const batchRequest = ({ dealId, batchId }: BatchParams) => ({
	headers: dealHeaders(dealId),
	path: { batchId },
});

/** A request to something in the batch, `path` naming it. */
export const batchItemRequest = <TPath extends object>(params: BatchParams, path: TPath) => {
	const request = batchRequest(params);
	return { ...request, path: { ...request.path, ...path } };
};

/** The request sent straight to the client, which then throws on an error status. */
export const batchCall = (params: BatchParams) => ({
	...batchRequest(params),
	throwOnError: true as const,
});

export const batchItemCall = <TPath extends object>(params: BatchParams, path: TPath) => ({
	...batchItemRequest(params, path),
	throwOnError: true as const,
});

/** The body a call answers with. */
export const answerOf = <TData>(request: Promise<{ data: TData }>): Promise<TData> =>
	request.then(({ data }) => data);

/** The issuer a read is scoped to; none means all issuers. */
export const issuerQuery = ({ issuerId }: BatchParams) =>
	issuerId === undefined ? {} : { query: { issuerId } };

const TRANSACTIONS_LIST_ID = 'dealstackAiReviewTransactionsControllerListTransactions';

/** A generated key's single entry: the operation's `_id` and the request's headers, path and query. */
const keyOptions = (queryKey: QueryKey): Record<string, unknown> | null => {
	const [options] = queryKey;
	return isRecord(options) ? options : null;
};

/** A review read of batch `batchId`. The deal's batch lists, ingestion progress and provenance are not. */
export const readsBatch = (queryKey: QueryKey, batchId: string): boolean => {
	const options = keyOptions(queryKey);
	return (
		typeof options?._id === 'string' &&
		options._id.startsWith('dealstackAi') &&
		isRecord(options.path) &&
		options.path.batchId === batchId
	);
};

/** The page a transactions-list key reads (page 1 is sent without `page`), or null for any other key. */
export const transactionsPageOf = (queryKey: QueryKey): number | null => {
	const options = keyOptions(queryKey);
	if (options?._id !== TRANSACTIONS_LIST_ID) {
		return null;
	}
	const page = isRecord(options.query) ? options.query.page : undefined;
	return typeof page === 'number' ? page : 1;
};

/** What every page of one transactions list shares: its key without the page; null for any other key. */
export const transactionsListHash = (queryKey: QueryKey): string | null => {
	const options = keyOptions(queryKey);
	if (options?._id !== TRANSACTIONS_LIST_ID) {
		return null;
	}
	return hashKey([
		{ ...options, query: omit(isRecord(options.query) ? options.query : {}, 'page') },
	]);
};
